import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Descriptions, Input, Modal, Row, Select, Space, Table, Tag, Typography } from "antd";
import { EyeOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";

const { Text, Title } = Typography;

const PickedBookingsManager = () => {
  const { request } = useRequest();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const agentId = profile?.agent_id;

  const fetchAgentBookings = async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      request({
        url: apiPath.agentBookingsByAgent(agentId),
        method: "GET",
        onSuccess: (res) => {
          if (res?.success && Array.isArray(res.data)) {
            setRows(res.data);
            return;
          }
          setRows([]);
        },
        onError: () => setRows([]),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgentBookings();
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((item) => {
      const q = searchText.trim().toLowerCase();
      const matchesSearch =
        !q ||
        String(item.Booking_Id || "").toLowerCase().includes(q) ||
        String(item.invoice_number || "").toLowerCase().includes(q) ||
        String(item.customer_name || "").toLowerCase().includes(q) ||
        String(item.customer_email || "").toLowerCase().includes(q) ||
        String(item.Hotel_name || "").toLowerCase().includes(q);

      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      const matchesPayment = paymentFilter === "all" || item.payment_status === paymentFilter;
      return matchesSearch && matchesStatus && matchesPayment;
    });
  }, [rows, searchText, statusFilter, paymentFilter]);

  const stats = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r) => r.status === "pending").length;
    const confirmed = rows.filter((r) => r.status === "confirmed").length;
    const paid = rows.filter((r) => r.payment_status === "paid").length;
    return { total, pending, confirmed, paid };
  }, [rows]);

  const columns = [
    { title: "Booking ID", dataIndex: "Booking_Id", key: "Booking_Id" },
    { title: "Invoice", dataIndex: "invoice_number", key: "invoice_number", render: (v) => v || "-" },
    { title: "Passenger Name", dataIndex: "customer_name", key: "customer_name", render: (v) => v || "-" },
    { title: "Hotel Name", dataIndex: "Hotel_name", key: "Hotel_name", render: (v) => v || "-" },
    {
      title: "Check-in Date",
      dataIndex: "checkIn",
      key: "checkIn",
      render: (v) => (v ? moment(v).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Booked Room",
      dataIndex: "number_of_Rooms",
      key: "number_of_Rooms",
      render: (v) => Number(v || 0),
    },
    { title: "Amount", dataIndex: "room_fare", key: "room_fare", render: (v) => `$. ${Number(v || 0)}` },
    {
      title: "Booking Status",
      dataIndex: "status",
      key: "status",
      render: (v) => <Tag color={v === "confirmed" ? "green" : "orange"}>{String(v || "pending").toUpperCase()}</Tag>,
    },
    {
      title: "Payment",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (v) => <Tag color={v === "paid" ? "green" : v === "failed" ? "red" : "gold"}>{String(v || "pending").toUpperCase()}</Tag>,
    },
    {
      title: "Booking Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => (v ? moment(v).format("DD MMM YYYY, hh:mm A") : "-"),
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedBooking(record);
            setDetailModalOpen(true);
          }}
        />
      ),
    },
  ];

  return (
    <Card bordered={false}>
      <Title level={4} style={{ marginBottom: 4 }}>Picked Booking Manager</Title>
      <Text type="secondary">Track your picked enquiry bookings and payment status.</Text>

      <Row gutter={12} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col><Tag color="blue">Total: {stats.total}</Tag></Col>
        <Col><Tag color="orange">Pending: {stats.pending}</Tag></Col>
        <Col><Tag color="green">Confirmed: {stats.confirmed}</Tag></Col>
        <Col><Tag color="cyan">Paid: {stats.paid}</Tag></Col>
      </Row>

      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search booking, invoice, customer, hotel"
          prefix={<SearchOutlined />}
          style={{ width: 320 }}
        />
        <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
          <Select.Option value="all">All Status</Select.Option>
          <Select.Option value="pending">Pending</Select.Option>
          <Select.Option value="confirmed">Confirmed</Select.Option>
          <Select.Option value="cancelled">Cancelled</Select.Option>
        </Select>
        <Select value={paymentFilter} onChange={setPaymentFilter} style={{ width: 160 }}>
          <Select.Option value="all">All Payments</Select.Option>
          <Select.Option value="pending">Pending</Select.Option>
          <Select.Option value="paid">Paid</Select.Option>
          <Select.Option value="failed">Failed</Select.Option>
        </Select>
      </Space>

      <Table
        rowKey={(r) => r._id || r.Booking_Id}
        columns={columns}
        dataSource={filteredRows}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: 1000 }}
      />

      <Modal
        title="Booking Details"
        open={detailModalOpen}
        onCancel={() => {
          setDetailModalOpen(false);
          setSelectedBooking(null);
        }}
        footer={null}
        width={920}
      >
        {selectedBooking && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Booking ID">{selectedBooking.Booking_Id || "-"}</Descriptions.Item>
              <Descriptions.Item label="Invoice">{selectedBooking.invoice_number || "-"}</Descriptions.Item>
              <Descriptions.Item label="Passenger Name">{selectedBooking.customer_name || "-"}</Descriptions.Item>
              <Descriptions.Item label="Customer Email">{selectedBooking.customer_email || "-"}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selectedBooking.customer_phone || "-"}</Descriptions.Item>
              <Descriptions.Item label="Hotel Name">{selectedBooking.Hotel_name || "-"}</Descriptions.Item>
              <Descriptions.Item label="Room Type">{selectedBooking.roomType || "-"}</Descriptions.Item>
              <Descriptions.Item label="Booked Rooms">{selectedBooking.number_of_Rooms || 0}</Descriptions.Item>
              <Descriptions.Item label="Check-in Date">
                {selectedBooking.checkIn ? moment(selectedBooking.checkIn).format("DD MMM YYYY") : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Check-out Date">
                {selectedBooking.checkOut ? moment(selectedBooking.checkOut).format("DD MMM YYYY") : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Booking Status">
                <Tag color={selectedBooking.status === "confirmed" ? "green" : "orange"}>
                  {String(selectedBooking.status || "pending").toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Status">
                <Tag color={selectedBooking.payment_status === "paid" ? "green" : selectedBooking.payment_status === "failed" ? "red" : "gold"}>
                  {String(selectedBooking.payment_status || "pending").toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Amount">$. {Number(selectedBooking.room_fare || 0)}</Descriptions.Item>
              <Descriptions.Item label="Booking Date">
                {selectedBooking.createdAt ? moment(selectedBooking.createdAt).format("DD MMM YYYY, hh:mm A") : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Need Pickup Service">
                {selectedBooking.need_pickup_service ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Need Drop Service">
                {selectedBooking.need_drop_service ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Pickup Details" span={2}>
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {selectedBooking.pickup_service_details || "-"}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Drop Details" span={2}>
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {selectedBooking.drop_service_details || "-"}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Airline Service Price">
                $. {Number(selectedBooking.airline_service_price || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="Invoice PDF" span={1}>
                {selectedBooking.invoice_url ? (
                  <a href={selectedBooking.invoice_url} target="_blank" rel="noreferrer">
                    Open Invoice
                  </a>
                ) : "-"}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 14 }}>
              <Text strong>Selected Services</Text>
              <div style={{ marginTop: 8 }}>
                {(selectedBooking.selected_services || []).length ? (
                  <Space wrap>
                    {selectedBooking.selected_services.map((service) => (
                      <Tag key={service._id || service.service_key}>
                        {service.label || service.service_key} - $. {Number(service.amount || 0)}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  <Text type="secondary">No selected services</Text>
                )}
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <Text strong>Invoice Breakdown</Text>
              <Descriptions bordered size="small" column={2} style={{ marginTop: 8 }}>
                <Descriptions.Item label="Base Rate">
                  $. {Number(selectedBooking.invoice_breakdown?.base_rate || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Nights">
                  {Number(selectedBooking.invoice_breakdown?.nights || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Rooms">
                  {Number(selectedBooking.invoice_breakdown?.rooms || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Stay Amount">
                  $. {Number(selectedBooking.invoice_breakdown?.stay_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Selected Extra Amount">
                  $. {Number(selectedBooking.invoice_breakdown?.selected_extra_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Transport Service Amount">
                  $. {Number(selectedBooking.invoice_breakdown?.transport_service_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Service Fee">
                  $. {Number(selectedBooking.invoice_breakdown?.service_fee || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Tax Amount">
                  $. {Number(selectedBooking.invoice_breakdown?.tax_amount || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Total" span={2}>
                  <Text strong>$. {Number(selectedBooking.invoice_breakdown?.total || selectedBooking.room_fare || 0)}</Text>
                </Descriptions.Item>
              </Descriptions>
            </div>
          </>
        )}
      </Modal>
    </Card>
  );
};

export default PickedBookingsManager;
